import { useForm } from 'react-hook-form';

import CustomModal from '@/components/CustomModal';
import TextArea from '@/components/TextArea';
import { ERoleAccess } from '@/enum';
import useAccess from '@/hooks/useAccess';
import { useFormatText } from '@/hooks/useFormatText';
import {
  discardSupportSchema,
  TDiscardSupportFormValues,
} from '@/schema/supportSchema';
import { zodResolver } from '@hookform/resolvers/zod';

interface IDiscardModalProps {
  setIsOpen: (isOpen: boolean) => void;
  onDiscard: (reason: string) => void;
  isLoading?: boolean;
}

const DiscardModal = ({
  setIsOpen,
  onDiscard,
  isLoading,
}: IDiscardModalProps) => {
  const { hasAccess } = useAccess();

  const { formatText } = useFormatText();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<TDiscardSupportFormValues>({
    resolver: zodResolver(discardSupportSchema),
    defaultValues: {
      reason: '',
    },
  });

  const onSubmit = (values: TDiscardSupportFormValues) => {
    onDiscard(values.reason);
  };

  if (!hasAccess(ERoleAccess.SUPPORT_QUERIES)) return null;

  return (
    <CustomModal
      setIsOpen={setIsOpen}
      isFooter
      confirmButtonProps={{
        label: formatText('support.discard-modal.discard-btn'),
        onClick: handleSubmit(onSubmit),
        loading: isLoading,
        className: 'bg-red-500! border-red-500! hover:bg-error-secondary/90!',
      }}
      cancelButtonProps={{
        label: formatText('common.actions.cancel'),
        onClick: () => setIsOpen(false),
        desructive: true,
        className: 'px-1.5!',
      }}
      className="w-150 max-w-[90vw]"
    >
      <div className="flex flex-col gap-4 w-full">
        <h2 className="text-neutral-primary text-xl font-semibold">
          {formatText('support.discard-modal.title')}
        </h2>
        <p className="text-neutral-secondary text-base">
          {formatText('support.discard-modal.sub-title')}
        </p>
        <TextArea
          name="reason"
          label={formatText('support.discard-modal.reason-label')}
          placeholder={formatText('support.discard-modal.reason-placeholder')}
          maxLength={250}
          showCount
          rows={5}
          register={register}
          error={
            errors.reason?.message ? formatText(errors.reason.message) : ''
          }
        />
      </div>
    </CustomModal>
  );
};

export default DiscardModal;
